import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Collapse,
    Box,
} from "@mui/material";
import { useState, useEffect } from "react";
import Editor from "@monaco-editor/react";
import { ExpandMore, ExpandLess } from "@mui/icons-material";

type ImportJsonDialogProps<T> = {
    open: boolean;
    onClose: () => void;
    onSubmit: (jsonData: T[]) => void;
    exampleJson: string; // stringified JSON array
};

export default function ImportJsonDialog<T>({
    open,
    onClose,
    onSubmit,
    exampleJson,
}: ImportJsonDialogProps<T>) {
    const [jsonText, setJsonText] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [showExample, setShowExample] = useState(false);

    useEffect(() => {
        if (!open) {
            setJsonText("");
            setError(null);
            setShowExample(false);
        }
    }, [open]);

    const validateJson = (value: string) => {
        try {
            const parsed = JSON.parse(value);

            if (!Array.isArray(parsed)) {
                return "JSON must be an array of objects.";
            }

            if (exampleJson) {
                const example = JSON.parse(exampleJson);
                if (!Array.isArray(example) || example.length === 0) {
                    return "Example JSON must be a non-empty array.";
                }

                const expectedKeys = Object.keys(example[0]).sort();
                const invalid = parsed.some((item) => {
                    const itemKeys = Object.keys(item).sort();
                    return JSON.stringify(itemKeys) !== JSON.stringify(expectedKeys);
                });

                if (invalid) {
                    return `Each object must have exactly these keys: ${expectedKeys.join(", ")}`;
                }
            }

            return null; // ✅ valid
        } catch {
            return "Invalid JSON format.";
        }
    };

    const handleChange = (value: string | undefined) => {
        const val = value || "";
        setJsonText(val);
        setError(validateJson(val));
    };

    const handleSubmit = () => {
        if (!error) {
            const parsed = JSON.parse(jsonText);
            onSubmit(parsed);
            setJsonText("");
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Import JSON</DialogTitle>
            <DialogContent>
                <Typography variant="body2" mb={2}>
                    Paste your JSON array below.
                </Typography>

                {exampleJson && (
                    <Box mb={2}>
                        <Button
                            size="small"
                            startIcon={showExample ? <ExpandLess /> : <ExpandMore />}
                            onClick={() => setShowExample((prev) => !prev)}
                        >
                            {showExample ? "Hide Example" : "Show Example"}
                        </Button>
                        <Collapse in={showExample}>
                            <Box
                                sx={{
                                    bgcolor: "grey.100",
                                    p: 2,
                                    borderRadius: 1,
                                    mt: 1,
                                    fontFamily: "monospace",
                                    fontSize: "0.85rem",
                                    whiteSpace: "pre-wrap",
                                }}
                            >
                                {exampleJson}
                            </Box>
                        </Collapse>
                    </Box>
                )}

                <Box
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        overflow: "hidden",
                        mb: 1,
                    }}
                >
                    <Editor
                        height="300px"
                        defaultLanguage="json"
                        value={jsonText}
                        onChange={handleChange}
                        options={{
                            minimap: { enabled: false },
                            wordWrap: "on",
                            formatOnPaste: true,
                            formatOnType: true,
                            fontSize: 14,
                        }}
                    />
                </Box>

                {error && (
                    <Typography variant="body2" color="error" mt={1}>
                        {error}
                    </Typography>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!!error || !jsonText}
                >
                    Import
                </Button>
            </DialogActions>
        </Dialog>
    );
}
